import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import { App, NavController, NavParams } from 'ionic-angular';
import { HttpErrorResponse } from '../../../node_modules/@angular/common/http';
import { BsDatepickerConfig, ModalDirective } from '../../../node_modules/ngx-bootstrap';
import { AccountHolder } from '../../app/models/AccountHolder';
import { AlertsHolder } from '../../app/models/AlertsHolder';
import { Card } from '../../app/models/Card';
import { CardBlockReason } from '../../app/models/CardBlockReason';
import { CardStatus } from '../../app/models/CardStatus';
import { PoS } from '../../app/models/PoS';
import { Config } from '../../providers/config';
import { ConfigService } from '../../providers/config/config.service';
import { NavControllerService } from '../../providers/nav-controller/navControllerService';
import { PaytronBackendService } from '../../providers/paytron-backend/paytron-backend.service';
import { Utils } from '../../providers/paytron-backend/utils';
import { TableService } from '../../services/table.service';
import { GenericQueryParams } from '../../utils/utils';
import { cardsTableConfig } from './cardsTableConfig';

@Component({
  selector: 'page-cards',
  templateUrl: 'cards.html',
})
export class CardsPage implements OnDestroy, OnInit {
  @ViewChild('cardModal')
  private cardModal: ModalDirective;

  private pageName: string = 'page-cards';

  private currentCard = new Card();
  private edit = true;
  private statuses = CardStatus;
  private notes = CardBlockReason;
  private activDate = new Date();
  private expirDate = new Date();
  private pos: PoS;
  public tableHeaders = cardsTableConfig;
  private navControllerService: NavControllerService;
  public bsConfig: Partial<BsDatepickerConfig> = new BsDatepickerConfig();
  private currentConfiguration: Config;
  private alertsHolder: AlertsHolder;
  private cardReaderWebSocket: WebSocket;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public storage: Storage,
    private backendService: PaytronBackendService,
    private configService: ConfigService,
    private utils: Utils,
    private app: App,
    private translate: TranslateService,
    public tableService: TableService
  ) {
    this.navControllerService = new NavControllerService(app, storage);
    this.currentConfiguration = this.configService.getConfiguration();
    this.storage.get('PoS').then((pos: PoS) => {
      this.pos = pos;
    });
    this.alertsHolder = new AlertsHolder(translate);
  }

  ngOnInit(): void {
    this.bsConfig = this.currentConfiguration.datepickerConfig;
  }

  ionViewDidEnter() {
    this.openCardListenerWebSocket();
  }

  cardStatus(status) {
    let classes = {
      'card-active': Object.is(CardStatus.Active, CardStatus[status]),
      'card-inactive': Object.is(CardStatus.Inactive, CardStatus[status]),
      'card-blocked': Object.is(CardStatus.Blocked, CardStatus[status]),
    };
    return classes;
  }

  editCard(card) {
    this.currentCard = card;
    this.edit = true;
    this.activDate = new Date(this.currentCard.activationDate);
    this.expirDate = this.currentCard.expirationDate ? new Date(this.currentCard.expirationDate) : null;
    this.showCardModal();
  }
  cardsQueryFn() {
    return (params: GenericQueryParams) => this.backendService.getPaginatedCards(params);
  }

  submitCard(btnEvent: HTMLButtonElement) {
    btnEvent.disabled = true;
    this.backendService
      .getCurrentAccountByCard(this.currentCard)
      .subscribe(
        (account) => {
          this.currentCard.currentAccountUuid = account.uuid;
          this.currentCard.posUuid = this.pos.uuid;
          this.currentCard.activationDate = this.activDate.toISOString();
          this.currentCard.expirationDate = this.expirDate ? this.expirDate.toISOString() : null;
          this.backendService.saveOrUpdateCard(this.currentCard).subscribe(
            (card) => {
              this.currentCard = card;
            },
            (err: HttpErrorResponse) => {}
          );
        },
        (err: HttpErrorResponse) => {}
      )
      .add(() => {
        this.closeCardModal();
        btnEvent.disabled = false;
      });
  }

  onStatusChange(status) {
    if (CardStatus[status] == CardStatus.Active.toString()) {
      this.currentCard.notes = '';
      this.currentCard.blacklisted = false;
    }
    if (CardStatus[status] == CardStatus.Inactive.toString() || CardStatus[status] == CardStatus.Blocked.toString()) {
      this.currentCard.notes = CardBlockReason[CardBlockReason.BlockedCard];
      this.currentCard.blacklisted = true;
    }
  }

  showCardModal() {
    this.utils.reformatAllCardInputDateStrings(this.currentCard);
    this.cardModal.show();
  }

  closeCardModal() {
    this.currentCard = new Card();
    this.tableService.refetch();
    this.cardModal.hide();
  }

  showPrintCardPage(card) {
    this.navControllerService.showPrintCardPage(card.uuid);
  }

  showCardDetails(card: Card) {
    let owner = card.type.service ? AccountHolder.User : AccountHolder.Customer;
    this.navControllerService.showCardDetails(card.uuid, owner);
  }

  showCouponsPage(card) {
    this.navControllerService.showCouponsPage(card.uuid);
  }

  private openCardListenerWebSocket() {
    if (this.cardReaderWebSocket) {
      this.cardReaderWebSocket.close();
    }
    let webSocket: WebSocket = (this.cardReaderWebSocket = new WebSocket(
      this.configService.getCardReaderWebSocketUrl()
    ));
    webSocket.addEventListener('error', (ev: Event) => {
      if (webSocket.readyState == WebSocket.CLOSED) {
        this.alertsHolder.show('Card reader not found. Set it up and retry.', 'danger');
      } else if (webSocket.readyState == WebSocket.CONNECTING) {
        this.alertsHolder.show('Card reader still connecting. Please wait.', 'danger');
      } else {
        this.alertsHolder.show('Error while reading the card.', 'danger');
        console.error('Card reader error', ev);
      }
    });
    webSocket.addEventListener('message', (ev: MessageEvent) => {
      let evData: any = JSON.parse(ev.data);
      switch (evData.messageType) {
        case 'event':
          switch (evData.message.event) {
            case 'UNPROTECTED_CARD_DETECTED':
              this.alertsHolder.show('Card unrecognized.', 'danger');
              this.sendCardReaderHaltCommand(webSocket);
              break;
            case 'CARD_DETECTED':
              let detectedCardId = evData.message.values.cardId;
              this.sendCardReaderHaltCommand(webSocket);
              this.backendService.getCard(detectedCardId).subscribe(
                (card: Card) => {
                  let accountHolder: AccountHolder = card.type.service
                    ? AccountHolder['User']
                    : AccountHolder['Customer'];
                  this.redirectToCardDetails(card, accountHolder);
                  webSocket.close();
                },
                (error: any) => {
                  console.error(error);
                  this.alertsHolder.show('Server error.', 'danger');
                }
              );
              break;
          }
          break;
      }
    });
    webSocket.addEventListener('open', (ev: Event) => {
      this.sendCardReaderHaltCommand(webSocket);
    });
  }

  private sendCardReaderHaltCommand(cardReaderWebSocket: WebSocket) {
    cardReaderWebSocket.send(
      JSON.stringify({
        messageType: 'command',
        message: {
          command: 'HALT',
        },
      })
    );
  }

  private redirectToCardDetails(card: Card, accountHolder: AccountHolder) {
    this.navControllerService.showCardDetails(card.uuid, accountHolder);
  }

  getTitle(msg: string) {
    return this.translate.instant(msg);
  }

  ngOnDestroy(): void {
    if (this.cardReaderWebSocket) {
      this.cardReaderWebSocket.close();
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/cards/cards.ts